import type { BarSeriesOption, LineSeriesOption } from 'echarts/charts'
import type { DataZoomComponentOption, GridComponentOption, LegendComponentOption, ToolboxComponentOption, TooltipComponentOption } from 'echarts/components'
import type { ComposeOption } from 'echarts/core'
import type { SetRequiredDeep } from 'type-fest'
import * as zod from 'zod'
import type { DepartmentPageLine } from '../Device'
import type { DateFormat } from '../PersonalSettings'
import { COMMON_WIDGET_DATA_CONSTRUCTOR, COMMON_WIDGET_ELEMENT_DATA, defaultInterval, IntervalLockSupportSchema, IntervalSupportSchema, LINE_CHART, MarkableWidgetSchema, TooltipCompareTypeSchema, WidgetElementBufferSupportSchema, WidgetElementSchema, WidgetSchema, WidgetSerieTypeSchema, type AbstractWidgetData, type BaseWidgetElementDialogProps, type GraphWidgetElementData, type IntervalSupport, type MarkableWidget, type PartialWidget, type PartialWidgetElement, type TooltipCompareType, type WidgetBufferValue, type WidgetConstructor, type WidgetSerieType } from './Common'
import type { BaseWidgetDataRequest, WidgetDataResponse } from './Widgets'

export type LineChartOptions = ComposeOption<
  LineSeriesOption
  | BarSeriesOption
  | TooltipComponentOption
  | GridComponentOption
  | ToolboxComponentOption
  | LegendComponentOption
  | DataZoomComponentOption
>
export interface LineChartWidgetOptionsResponse extends WidgetDataResponse {
  type: 'LINE_CHART'
  options: LineChartOptions
  compareTypesTooltip: Map<string, TooltipCompareType>
}

export type LineChartWidgetDataRequest = BaseWidgetDataRequest & {
  type: 'LINE_CHART'
  widget: LineChartWidget
  dateFormat: DateFormat
}

export const LineChartWidgetElementSchema = WidgetElementSchema.merge(WidgetElementBufferSupportSchema).extend({
  type: LINE_CHART,
  color: zod.string().nullable(),
  showAverageValue: zod.boolean(),
  showLowestValue: zod.boolean(),
  showHighestValue: zod.boolean(),
  serieType: WidgetSerieTypeSchema,
  compareTypeTooltip: TooltipCompareTypeSchema,
})
export type LineChartWidgetElement = zod.infer<typeof LineChartWidgetElementSchema>
export type LineChartWidgetElementPartial = SetRequiredDeep<PartialWidgetElement<LineChartWidgetElement>, 'type'>

export const LineChartWidgetSchema = WidgetSchema.merge(MarkableWidgetSchema).merge(IntervalSupportSchema).merge(IntervalLockSupportSchema).extend({
  type: LINE_CHART,
  elements: LineChartWidgetElementSchema.array(),
})
export type LineChartWidget = zod.infer<typeof LineChartWidgetSchema>
export type LineChartWidgetPartial = SetRequiredDeep<PartialWidget<LineChartWidget>, 'type' | 'elements' | `elements.${number}.type`>

export interface LineChartWidgetElementData extends GraphWidgetElementData {
  showAverageValue: boolean
  showLowestValue: boolean
  showHighestValue: boolean
  serieType: WidgetSerieType
  compareTypeTooltip: TooltipCompareType
  values: WidgetBufferValue[]
  compareToValues: WidgetBufferValue[]
  availableUnits: Record<string, string>
}
export interface LineChartWidgetData extends AbstractWidgetData<LineChartWidgetElementData>, MarkableWidget, IntervalSupport {
  type: 'LINE_CHART'
  elements: LineChartWidgetElementData[]
  timestamps: string[]
  compareToTimestamps: string[]
}
export interface LineChartWidgetElementDialogProps extends BaseWidgetElementDialogProps {
  compareEnabled: boolean
  line?: DepartmentPageLine
  allowBufferCreation: boolean
  type: 'LINE_CHART'
}
export const DEFAULT_LINE_CHART_WIDGET_ELEMENT: LineChartWidgetElementPartial = {
  ...COMMON_WIDGET_ELEMENT_DATA,
  color: null,
  showAverageValue: false,
  showHighestValue: false,
  showLowestValue: false,
  compareTypeTooltip: 'NONE',
  serieType: 'line',
  pcNumber: null,
  useDefaultPcNumber: true,
  type: 'LINE_CHART',
  buffer: null,
}
export const LINE_CHART_WIDGET_CONSTRUCTOR: WidgetConstructor<LineChartWidgetPartial> = () => ({
  type: 'LINE_CHART',
  ...COMMON_WIDGET_DATA_CONSTRUCTOR(),
  interval: defaultInterval(),
  markAreaElements: [],
  elements: [DEFAULT_LINE_CHART_WIDGET_ELEMENT],
})
